import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
import { startOfWeek } from 'date-fns/startOfWeek';
import { getDay } from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Calendar, Clock, MapPin, Users, IndianRupee, Search, List, User
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SEO } from '../components/SEO';
import { TrainingRegistrationForm } from '../components/TrainingRegistrationForm';
import { useToast } from '../hooks/use-toast';
import { getImageUrl } from '../utils/imageUrl';
import axios from 'axios';
import './CalendarStyles.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const locales = {
  'en-US': enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export const TrainingCalendarPublic = () => {
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMode, setSelectedMode] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('calendar');
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const { toast } = useToast();

  const modes = ['All', 'Classroom', 'Online', 'Hybrid', 'On-Site'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterTrainings();
  }, [trainings, searchQuery, selectedMode, selectedCategory]);

  const fetchData = async () => {
    try {
      const [trainingsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/public/training-calendar`),
        axios.get(`${API}/public/training-categories`)
      ]);
      
      setTrainings(trainingsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTrainings = () => {
    let filtered = trainings;

    if (searchQuery) {
      filtered = filtered.filter(training =>
        training.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        training.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedMode !== 'All') {
      filtered = filtered.filter(training => training.mode === selectedMode);
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(training => training.category?.id === parseInt(selectedCategory));
    }

    setFilteredTrainings(filtered);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Removed unused getDaysUntil function

  const handleSelectEvent = (event) => {
    setSelectedTraining(event.training);
    setIsDetailOpen(true);
  };

  const handleRegister = (training) => {
    // Check if using external registration link
    if (training.use_internal_form === false && training.registration_link) {
      window.open(training.registration_link, '_blank');
    } else {
      // Open internal registration form
      setIsDetailOpen(false); // Close detail dialog first
      setIsRegistrationOpen(true);
    }
  };

  const handleRegistrationSuccess = (data) => {
    console.log('Registration successful:', data);
    // Refresh trainings to update seat count
    fetchData();
    // Close registration form
    setIsRegistrationOpen(false);
    // Show a success toast
    toast({ title: 'Registration confirmed', description: 'Your registration has been received. You will receive a confirmation email shortly.' });
  };

  const calendarEvents = filteredTrainings.map(training => ({
    id: training.id,
    title: training.title,
    start: new Date(training.date),
    end: new Date(training.date),
    resource: {
      mode: training.mode,
      location: training.location,
      price: training.price,
      seats: training.seats_available
    },
    training: training
  }));

  const eventStyleGetter = (event) => {
    const modeColors = {
      'Classroom': { backgroundColor: '#3B82F6', color: 'white' },
      'Online': { backgroundColor: '#10B981', color: 'white' },
      'Hybrid': { backgroundColor: '#F59E0B', color: 'white' },
      'On-Site': { backgroundColor: '#8B5CF6', color: 'white' }
    };
    
    return {
      style: modeColors[event.resource.mode] || { backgroundColor: '#3B82F6', color: 'white' }
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  // Generate SEO data based on filters
  const getSEOData = () => {
    let title = 'Training Calendar - ISDE Academy';
    let description = 'Browse our upcoming training sessions and courses. Professional development programs in classroom, online, and hybrid formats.';
    let keywords = 'ISDE Academy training calendar, upcoming courses, professional development, training schedule';

    if (selectedCategory !== 'All') {
      const category = categories.find(c => c.name === selectedCategory);
      if (category) {
        title = `${category.name} Training Calendar - ISDE Academy`;
        description = category.meta_description || `Upcoming ${category.name} training sessions at ISDE Academy. ${category.description || ''}`;
        keywords = category.meta_keywords || `${category.name}, ISDE Academy, training calendar, professional development`;
      }
    } else if (selectedMode !== 'All') {
      title = `${selectedMode} Training Calendar - ISDE Academy`;
      description = `Browse our upcoming ${selectedMode.toLowerCase()} training sessions. Professional development programs in ${selectedMode.toLowerCase()} format.`;
      keywords = `${selectedMode} training, ISDE Academy, training calendar, ${selectedMode.toLowerCase()} courses`;
    }

    return { title, description, keywords };
  };

  const seoData = getSEOData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pt-20">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        type="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "ISDE Academy",
          "description": seoData.description,
          "url": window.location.href,
          "numberOfEmployees": filteredTrainings.length,
          "event": filteredTrainings.slice(0, 5).map(training => ({
            "@type": "EducationEvent",
            "name": training.title,
            "description": training.description,
            "startDate": training.start_date,
            "endDate": training.end_date,
            "location": {
              "@type": "Place",
              "name": training.location || "ISDE Academy"
            },
            "organizer": {
              "@type": "Organization",
              "name": "ISDE Academy",
              "url": "https://isde.academy"
            }
          }))
        }}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white py-20 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-lg rounded-2xl mb-4"
            >
              <Calendar className="h-10 w-10 text-white" />
            </motion.div>
            <h1 className="text-5xl lg:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Training Calendar
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Browse and book upcoming trainings in calendar or list format. Find the perfect training schedule for you.
            </p>
            <div className="flex flex-wrap gap-4 justify-center items-center pt-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm">Live Schedule</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Clock className="h-4 w-4" />
                <span className="text-sm">Real-time Updates</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1 relative w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    placeholder="Search trainings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => setViewMode('calendar')}
                    variant={viewMode === 'calendar' ? 'default' : 'outline'}
                    className={viewMode === 'calendar' ? 'bg-blue-600' : ''}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Calendar
                  </Button>
                  <Button
                    onClick={() => setViewMode('list')}
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    className={viewMode === 'list' ? 'bg-blue-600' : ''}
                  >
                    <List className="h-4 w-4 mr-2" />
                    List
                  </Button>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Mode</p>
                <div className="flex flex-wrap gap-2">
                  {modes.map(mode => (
                    <Button
                      key={mode}
                      onClick={() => setSelectedMode(mode)}
                      size="sm"
                      variant={selectedMode === mode ? 'default' : 'outline'}
                      className={selectedMode === mode ? 'bg-blue-600' : ''}
                    >
                      {mode}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Category</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => setSelectedCategory('All')}
                    size="sm"
                    variant={selectedCategory === 'All' ? 'default' : 'outline'}
                    className={selectedCategory === 'All' ? 'bg-blue-600' : ''}
                  >
                    All
                  </Button>
                  {categories.map(cat => (
                    <Button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id.toString())}
                      size="sm"
                      variant={selectedCategory === cat.id.toString() ? 'default' : 'outline'}
                      className={selectedCategory === cat.id.toString() ? 'bg-blue-600' : ''}
                    >
                      {cat.title}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mb-4">
          <p className="text-lg text-gray-700">
            <span className="font-bold text-blue-600">{filteredTrainings.length}</span>
            {' '}training{filteredTrainings.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Views */}
        <AnimatePresence mode="wait">
          {viewMode === 'calendar' ? (
            <motion.div key="calendar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Card className="shadow-xl">
                <CardContent className="p-6">
                  <div style={{ height: '700px' }}>
                    <BigCalendar
                      localizer={localizer}
                      events={calendarEvents}
                      startAccessor="start"
                      endAccessor="end"
                      date={currentDate}
                      onNavigate={(date) => setCurrentDate(date)}
                      onSelectEvent={handleSelectEvent}
                      eventPropGetter={eventStyleGetter}
                      views={['month', 'week', 'agenda']}
                      defaultView="month"
                      popup
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTrainings.map((training) => (
                  <Card 
                    key={training.id}
                    className="hover:shadow-xl transition-all cursor-pointer overflow-hidden"
                    onClick={() => { setSelectedTraining(training); setIsDetailOpen(true); }}
                  >
                    {training.poster_url && (
                      <div className="relative h-48 w-full overflow-hidden bg-gray-200">
                        <img
                          src={getImageUrl(training.poster_url)}
                          alt={training.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className="flex justify-between mb-4">
                        <Badge className="bg-blue-100 text-blue-700">{training.mode}</Badge>
                      </div>
                      <h3 className="text-xl font-bold mb-2 line-clamp-2">{training.title}</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-600" />
                          <span>{formatDate(training.date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <span>{training.duration}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-blue-600" />
                          <span>{training.location}</span>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t">
                        {training.is_paid === false ? (
                          <span className="text-2xl font-bold text-green-600">Complimentary</span>
                        ) : (
                          <span className="text-2xl font-bold text-blue-600">{training.price}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Detail Dialog */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedTraining && (
              <div className="space-y-6">
                {selectedTraining.poster_url && (
                  <div className="relative w-full h-64 -mt-6 -mx-6 mb-4 overflow-hidden rounded-t-lg">
                    <img
                      src={getImageUrl(selectedTraining.poster_url)}
                      alt={selectedTraining.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <DialogHeader>
                  <DialogTitle className="text-3xl font-bold">{selectedTraining.title}</DialogTitle>
                  <Badge className="w-fit">{selectedTraining.mode}</Badge>
                </DialogHeader>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <Calendar className="h-6 w-6 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-600">Date</p>
                        <p className="font-semibold">{formatDate(selectedTraining.date)}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Clock className="h-6 w-6 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-600">Duration</p>
                        <p className="font-semibold">{selectedTraining.duration}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <MapPin className="h-6 w-6 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-semibold">{selectedTraining.location}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <Users className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="text-sm text-gray-600">Seats</p>
                        <p className="font-semibold text-green-600">{selectedTraining.seats_available}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <IndianRupee className="h-6 w-6 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-600">Price</p>
                        {selectedTraining.is_paid === false ? (
                          <p className="font-semibold text-green-600 text-2xl">Complimentary</p>
                        ) : (
                          <p className="font-semibold text-blue-600 text-2xl">{selectedTraining.price}</p>
                        )}
                      </div>
                    </div>
                    {selectedTraining.instructor && (
                      <div className="flex gap-3">
                        <User className="h-6 w-6 text-blue-600" />
                        <div>
                          <p className="text-sm text-gray-600">Instructor</p>
                          <p className="font-semibold">{selectedTraining.instructor}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {selectedTraining.description && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-bold text-lg mb-3">About</h3>
                    <p className="text-gray-700 whitespace-pre-line">{selectedTraining.description}</p>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleRegister(selectedTraining)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                    disabled={selectedTraining.seats_available <= 0}
                  >
                    {selectedTraining.seats_available <= 0 ? 'Fully Booked' :
                     selectedTraining.use_internal_form === false && selectedTraining.registration_link ? 'Register Now (External)' :
                     selectedTraining.is_paid ? `Register (${selectedTraining.price})` :
                     'Register for Free'}
                  </Button>
                  <Button onClick={() => setIsDetailOpen(false)} variant="outline">Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Training Registration Form */}
        <TrainingRegistrationForm
          training={selectedTraining}
          isOpen={isRegistrationOpen}
          onClose={() => setIsRegistrationOpen(false)}
          onSuccess={handleRegistrationSuccess}
        />
      </div>
    </div>
  );
};
